// src/pages/platform/PlatformEditPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

/**
 * Platform edit page.
 */
export const PlatformEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = usePlatform();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
  });

  useEffect(() => {
    if (id) {
      store.GetPlatform(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  useEffect(() => {
    if (store.currentItem) {
      setFormData({
        name: (store.currentItem as any).name ?? '',
        slug: (store.currentItem as any).slug ?? '',
        description: (store.currentItem as any).description ?? '',
      });
    }
  }, [store.currentItem]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);

    try {
      await store.UpdatePlatform(id, formData as any);
      navigate(`/platforms/${id}`);
    } catch {
      // Error is handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  const platformName = (store.currentItem as any)?.name ?? '';

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm">
          <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
            {t('platform.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/platforms/${id}`} className="text-muted-foreground hover:text-foreground">
            {platformName || t('platform.details')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.edit')}</span>
        </nav>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('platform.edit')}</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('platform.details')}</CardTitle>
            <CardDescription>{t('platform.updateInfo')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label={t('platform.name')}
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('platform.namePlaceholder')}
              required
            />

            <div>
              <Input
                label={t('platform.slug')}
                type="text"
                value={formData.slug}
                disabled
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {t('platform.slugReadonly')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('platform.description')}
              </label>
              <textarea
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px]"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={t('platform.descriptionPlaceholder')}
                rows={3}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to={`/platforms/${id}`}>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default PlatformEditPage;
